import {SxProps, Theme} from '@mui/material';

export const containerSx: SxProps<Theme> = {
    // Its own cap, narrower than the layout's: the page is a grid of hours by days, and every extra
    // pixel of width goes into cells that are already wide enough. Raised to 1700 so a wide monitor
    // gets more of the week on screen without the container stopping 400px before the page does.
    maxWidth: '1800px',
    margin: '0 auto',
    marginTop: '20px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
};

export const headerSx: SxProps<Theme> = {
    backgroundColor: '#32324D',
    color: 'white',
    padding: '24px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

export const mainContentSx: SxProps<Theme> = {
    padding: '32px',
    // 32px each side is 64px of a 390px screen - a sixth of the week gone before the grid starts.
    // `minWidth: 0` for the same reason the matrix below needs it: this block is what passes the
    // available width down, and a block that will not shrink hands its children the full 1060px.
    minWidth: 0,
    '@media (max-width: 899.95px)': {
        padding: '12px',
    },
};

export const defaultPriceSectionSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '32px',
    padding: '20px',
    backgroundColor: '#F5F5F7',
    borderRadius: '12px',
    border: '1px solid #EDEDF2',
};

export const defaultPriceLabelSx: SxProps<Theme> = {
    fontSize: '16px',
    fontWeight: 600,
    color: '#32324D',
};

export const priceInputContainerSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
};

export const defaultPriceInputSx: SxProps<Theme> = {
    padding: '12px 16px',
    border: '2px solid #007fdc',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 600,
    width: '120px',
    textAlign: 'center',
    color: '#32324D',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    fontFamily: 'Poppins, sans-serif',
    MozAppearance: 'textfield',
    '&:focus': {
        outline: 'none',
        borderColor: '#006dc7',
        boxShadow: '0 0 0 4px rgba(0, 127, 220, 0.1)',
    },
    '&:hover': {
        backgroundColor: '#DEF0FC',
        transform: 'scale(1.02)',
    },
    '&::-webkit-outer-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
    },
    '&::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
    },
};

export const currencySx: SxProps<Theme> = {
    fontSize: '14px',
    color: '#9090B0',
    fontWeight: 500,
};

export const seasonsSectionSx: SxProps<Theme> = {
    display: 'grid',
    gap: '32px',
};

export const seasonCardSx = (color: string): SxProps<Theme> => ({
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '16px 20px',
    border: '1px solid #EDEDF2',
    borderLeft: `4px solid ${color}`,
});

export const seasonHeaderSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
    fontSize: '1.3rem',
    fontWeight: 600,
    color: '#32324D',
};

export const seasonIconSx: SxProps<Theme> = {
    fontSize: '1.1rem',
    marginRight: '10px',
};

export const monthsGridSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
};

export const monthCardSx: SxProps<Theme> = {
    backgroundColor: '#F5F5F7',
    borderRadius: '12px',
    padding: '12px 16px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    border: '2px solid #EDEDF2',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 24px rgba(0, 127, 220, 0.15)',
        borderColor: '#007fdc',
        backgroundColor: '#DEF0FC',
    },
    '&.selected': {
        borderColor: '#007fdc',
        backgroundColor: '#DEF0FC',
        boxShadow: '0 4px 16px rgba(0, 127, 220, 0.2)',
    },
};

export const monthNameSx: SxProps<Theme> = {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#32324D',
    marginBottom: '4px',
};

export const monthSubtitleSx: SxProps<Theme> = {
    fontSize: '0.8rem',
    color: '#9090B0',
    fontWeight: 400,
};

export const energyTypeSectionSx: SxProps<Theme> = {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '16px 20px',
    border: '1px solid #EDEDF2',
    borderLeft: '4px solid #32324D',
    marginBottom: '24px',
};

export const energyTypeHeaderSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
    fontSize: '1.3rem',
    fontWeight: 600,
    color: '#32324D',
};

export const energyTypesGridSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px',
};

export const energyTypeCardSx: SxProps<Theme> = {
    backgroundColor: '#F5F5F7',
    borderRadius: '8px',
    padding: '12px 16px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    border: '2px solid #EDEDF2',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 127, 220, 0.15)',
        borderColor: '#007fdc',
        backgroundColor: '#DEF0FC',
    },
    '&.selected': {
        borderColor: '#007fdc',
        backgroundColor: '#DEF0FC',
        boxShadow: '0 4px 12px rgba(0, 127, 220, 0.2)',
    },
};

export const dayViewSx: SxProps<Theme> = {
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid #EDEDF2',
};

export const dayHeaderSx: SxProps<Theme> = {
    textAlign: 'center',
    marginBottom: '24px',
};

export const dayTitleSx: SxProps<Theme> = {
    fontSize: '1.75rem',
    color: '#32324D',
    marginBottom: '8px',
    fontWeight: 600,
};

export const daySubtitleSx: SxProps<Theme> = {
    fontSize: '1rem',
    color: '#9090B0',
    fontWeight: 400,
};